import type { ICellRendererParams } from 'ag-grid-community';
import {
  NEWS_ARTICLE_STATUS_LABELS,
  type NewsArticle,
  type NewsArticleStatus,
} from '../../schema';

/**
 * Tonal pill with a small leading dot. Light tinted bg in the brand-tone, full-
 * saturation text, font-black + uppercase. Mirrors the "premium CRM" reference.
 *
 * Notes:
 *   - `inline-flex` + `w-fit` + `self-center` keep the pill content-sized
 *     even when the parent ag-grid cell uses `display: flex` with stretched
 *     line-height (which otherwise inflates a bare `inline-flex` to row height).
 *   - `leading-none` blocks line-height inheritance from the cell's
 *     `--ag-line-height: var(--ag-row-height)` default.
 *   - Tones differ from NewsArticleCard's mobile palette by design; labels
 *     are shared via NEWS_ARTICLE_STATUS_LABELS.
 */
const TONES: Record<
  NewsArticleStatus,
  { bg: string; text: string; dot: string }
> = {
  draft: { bg: 'bg-secondary/15', text: 'text-secondary', dot: 'bg-secondary' },
  published: { bg: 'bg-success/15', text: 'text-success', dot: 'bg-success' },
  archived: { bg: 'bg-warning/15', text: 'text-warning', dot: 'bg-warning' },
};

export function StatusBadge(p: ICellRendererParams<NewsArticle, NewsArticleStatus>) {
  const status = p.value;
  if (!status) return null;
  const s = TONES[status];

  return (
    <span
      className={[
        'inline-flex w-fit shrink-0 items-center gap-1.5 self-center rounded-full px-2 py-[3px]',
        'text-[10px] font-black uppercase leading-none tracking-tight',
        s.bg,
        s.text,
      ].join(' ')}
    >
      <span aria-hidden className={`h-1.5 w-1.5 shrink-0 rounded-full ${s.dot}`} />
      <span>{NEWS_ARTICLE_STATUS_LABELS[status]}</span>
    </span>
  );
}
